// Form that allows for dynamic form fields

import { MutableRefObject, ReactNode, useEffect, useRef } from 'react';
import {
  FieldArray,
  FieldArrayRenderProps,
  Formik,
  FormikContextType,
  FormikValues,
  setNestedObjectValues,
} from 'formik';
import { Form as AntForm } from 'fave-ui';

export interface FormProps<T> {
  formRef?: MutableRefObject<FormikContextType<T> | undefined>;
}

type DynamicForm<T> = {
  name: string;
  children: (props: {
    values: T;
    push: FieldArrayRenderProps['push'];
    remove: FieldArrayRenderProps['remove'];
  }) => ReactNode;
};

const ArrayForm = <T extends {}>({
  name,
  initialValues,
  onSubmit,
  validateOnMount,
  validationSchema,
  validateOnChange = false,
  validateOnSchemaChange = false,
  children,
  formRef,
  ...rest
}: DynamicForm<T> & FormikValues & FormProps<T>) => {
  const ref = useRef<FormikContextType<T>>();

  useEffect(() => {
    if (validateOnSchemaChange) ref.current?.validateForm();
    if (validateOnMount)
      ref.current?.validateForm().then(() =>
        ref.current?.setTouched(
          // set the touched state once validateForm is done
          setNestedObjectValues(ref.current.errors, true),
        ),
      );
  }, [ref, validateOnSchemaChange, validateOnMount]);

  const refPassthrough = (instance: FormikContextType<T>) => {
    ref.current = instance;

    if (formRef) formRef.current = instance;
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={validateOnChange}
      validateOnMount
      enableReinitialize
      innerRef={refPassthrough}
      {...rest}
    >
      {({ values }) => (
        <AntForm layout="vertical">
          <FieldArray name={name}>
            {({ push, remove }) => children({ values, push, remove })}
          </FieldArray>
        </AntForm>
      )}
    </Formik>
  );
};

export default ArrayForm;
